import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { gridSize } from '@atlaskit/theme';
import { useIntl } from '@atlassian/jira-intl';
import type { Dependency, IssueLinkType } from '../../../common/types';
import { IssueLinkTypeSelect } from '../../../common/ui/issue-link-type-select';
import { useAvailableIssueLinkTypes, useFormState, useIssueLinkType } from '../../../controllers';
import { messages } from './messages';

interface Props {
	dependency: Dependency;
}

export const DependencyListItem = ({ dependency }: Props) => {
	const [{ savingInProgress }] = useFormState();
	const [availableIssueLinkTypes] = useAvailableIssueLinkTypes();
	const [issueLinkType, { swapDirection, changeIssueLinkTypeId }] = useIssueLinkType({
		id: dependency.issueLinkTypeId,
	});
	const { formatMessage } = useIntl();

	const swap = useCallback(() => {
		swapDirection(dependency.issueLinkTypeId);
	}, [swapDirection, dependency.issueLinkTypeId]);

	const handleIssueLinkTypeSelectChange = useCallback(
		(next: IssueLinkType['id'] | undefined) => {
			changeIssueLinkTypeId(dependency.issueLinkTypeId, next);
		},
		[changeIssueLinkTypeId, dependency.issueLinkTypeId],
	);

	if (!issueLinkType) {
		return null;
	}

	return (
		<Container role="listitem">
			<IssueLinkTypeSelect
				options={[issueLinkType, ...availableIssueLinkTypes]}
				value={issueLinkType.id}
				onChange={handleIssueLinkTypeSelectChange}
				disabled={savingInProgress}
				aria-label={formatMessage(messages.issueLinkTypeLabel, {
					type: issueLinkType.name,
				})}
				clearable
			/>
			<Spacer />
			<div role="status" aria-live="polite">
				{dependency.direction === 'INWARD' &&
					formatMessage(messages.inwardDescription, {
						outwardDescription: issueLinkType.outwardDescription,
					})}
				{dependency.direction === 'OUTWARD' &&
					formatMessage(messages.outwardDescription, {
						outwardDescription: issueLinkType.outwardDescription,
					})}
			</div>

			<Button
				isDisabled={savingInProgress}
				appearance="link"
				onClick={swap}
				aria-label={formatMessage(messages.swapLabel, { name: issueLinkType.name })}
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={{ textDecoration: 'underline' }}
			>
				{formatMessage(messages.swap)}
			</Button>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	flexShrink: 0,
	cursor: 'default',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spacer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: gridSize(),
});
